import { inject, Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import {
  catchError,
  filter,
  from,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GraphAuthService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  public restoreSession(loginHint: string): Observable<void> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter(status => status === InteractionStatus.None),
      take(1),

      switchMap(() => {
        const instance = this.msalService.instance;

        // Check whether MSAL already knows the currently logged-in app user.
        const account = instance.getAccount({ loginHint });

        if (account) {
          instance.setActiveAccount(account);
          return of(undefined);
        }

        /*
         * No matching MSAL account exists.
         *
         * Remove accounts/tokens from previous users so that the MSAL
         * interceptor cannot fall back to an old account.
         */
        instance.setActiveAccount(null);

        return from(instance.clearCache()).pipe(
          switchMap(() =>
            this.msalService.ssoSilent({
              loginHint,
              redirectUri: `${location.origin}/msal-silent-redirect`,
            })
          ),

          tap(result => {
            if (result.account) {
              instance.setActiveAccount(result.account);
            }
          }),

          map(() => undefined),

          catchError(error => {
            /*
             * This is expected in browsers such as Firefox when third-party
             * cookies prevent silent SSO.
             *
             * The important part is that the previous user's MSAL cache has
             * already been cleared.
             */

            return of(undefined);
          })
        );
      }),

      catchError(() => {
        return of(undefined);
      })
    );
  }

  public async clearSession(): Promise<void> {
    this.msalService.instance.setActiveAccount(null);
    await this.msalService.instance.clearCache();
  }
}
